/**
 * @fileoverview Hook centralizado para llamadas API con storeId automático
 * Elimina la necesidad de manejar storeId manualmente en cada servicio
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useStoreId } from '../contexts/StoreContext';
import apiClient from '../services/apiClient';

/**
 * Hook para queries que requieren storeId automáticamente
 * @param {string} queryKey - Clave base de la query
 * @param {string} endpoint - Endpoint de la API (sin storeId)
 * @param {Object} options - Opciones adicionales de React Query
 * @param {Object} params - Parámetros adicionales para la query
 * @returns {Object} Resultado de useQuery con storeId inyectado
 */
export const useStoreQuery = (queryKey, endpoint, options = {}, params = {}) => {
  const { storeId, isLoading: storeIdLoading, error: storeIdError } = useStoreId();

  return useQuery({
    queryKey: [queryKey, storeId, params],
    queryFn: async () => {
      if (!storeId) {
        throw new Error('StoreId no disponible para la consulta');
      }

      const queryParams = new URLSearchParams({
        storeId,
        ...params
      });

      const response = await apiClient.get(`${endpoint}?${queryParams}`);
      return response.data;
    },
    enabled: !!storeId && !storeIdLoading && !storeIdError && (options.enabled !== false),
    staleTime: 5 * 60 * 1000, // 5 minutos por defecto
    cacheTime: 10 * 60 * 1000, // 10 minutos por defecto
    ...options
  });
};

/**
 * Hook para mutaciones que requieren storeId automáticamente
 * @param {string} endpoint - Endpoint de la API
 * @param {Object} options - Opciones adicionales de React Query
 * @returns {Object} Resultado de useMutation con storeId inyectado
 */
export const useStoreMutation = (endpoint, options = {}) => {
  const { storeId } = useStoreId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data = {}) => {
      if (!storeId) {
        throw new Error('StoreId no disponible para la mutación');
      }

      const payload = {
        storeId,
        ...data
      };

      const response = await apiClient.post(endpoint, payload);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      // Invalidar queries relacionadas automáticamente
      queryClient.invalidateQueries({ queryKey: [storeId] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    ...options
  });
};

/**
 * Hook específico para estadísticas del dashboard
 * @param {Object} dateRange - Rango de fechas { startDate, endDate }
 * @param {Object} options - Opciones adicionales
 * @returns {Object} Datos de estadísticas
 */
export const useStoreStats = (dateRange = {}, options = {}) => {
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  
  const defaultDateRange = {
    startDate: dateRange.startDate || thirtyDaysAgo.toISOString().split('T')[0],
    endDate: dateRange.endDate || today.toISOString().split('T')[0]
  };

  return useStoreQuery(
    'stats-summary',
    '/stats/summary',
    {
      staleTime: 2 * 60 * 1000, // 2 minutos para stats
      cacheTime: 5 * 60 * 1000, // 5 minutos para stats
      ...options
    },
    defaultDateRange
  );
};

/**
 * Hook específico para productos top
 * @param {Object} params - Parámetros adicionales
 * @param {Object} options - Opciones adicionales
 * @returns {Object} Datos de productos top
 */
export const useTopProducts = (params = {}, options = {}) => {
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  
  const defaultParams = {
    startDate: params.startDate || thirtyDaysAgo.toISOString().split('T')[0],
    endDate: params.endDate || today.toISOString().split('T')[0],
    limit: params.limit || 5,
    ...params
  };

  return useStoreQuery(
    'top-products',
    '/products/top',
    {
      staleTime: 5 * 60 * 1000, // 5 minutos
      cacheTime: 10 * 60 * 1000, // 10 minutos
      ...options
    },
    defaultParams
  );
};

/**
 * Hook específico para ventas diarias
 * @param {Object} params - Parámetros adicionales (startDate, endDate)
 * @param {Object} options - Opciones adicionales
 * @returns {Object} Datos de ventas diarias
 */
export const useDailySales = (params = {}, options = {}) => {
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  
  const defaultParams = {
    startDate: params.startDate || thirtyDaysAgo.toISOString().split('T')[0],
    endDate: params.endDate || today.toISOString().split('T')[0],
    ...params
  };

  return useStoreQuery(
    'daily-sales',
    '/stats/daily',
    {
      staleTime: 2 * 60 * 1000, // 2 minutos
      cacheTime: 5 * 60 * 1000, // 5 minutos
      ...options
    },
    defaultParams
  );
};

/**
 * Hook para sincronización de datos
 * @param {Object} options - Opciones adicionales
 * @returns {Object} Mutación de sincronización
 */
export const useSyncStore = (options = {}) => {
  return useStoreMutation('/sync/trigger', {
    onSuccess: () => {
      console.log('[useApiWithStore] Sincronización completada');
    },
    onError: (error) => {
      console.error('[useApiWithStore] Error en sincronización:', error);
    },
    ...options
  });
};

/**
 * Hook para obtener datos de diagnóstico
 * @param {Object} options - Opciones adicionales
 * @returns {Object} Datos de diagnóstico
 */
export const useStoreDiagnostic = (options = {}) => {
  const { storeId } = useStoreId();
  
  return useQuery({
    queryKey: ['diagnostic', storeId],
    queryFn: async () => {
      if (!storeId) {
        throw new Error('StoreId no disponible para diagnóstico');
      }

      const response = await apiClient.get(`/diagnostic/store/${encodeURIComponent(storeId)}/data?storeId=${encodeURIComponent(storeId)}`);
      return response.data;
    },
    enabled: !!storeId && (options.enabled !== false),
    staleTime: 30 * 1000, // 30 segundos para diagnóstico
    cacheTime: 60 * 1000, // 1 minuto para diagnóstico
    ...options
  });
};
